import React, { useEffect, useRef } from 'react';
import {
  Image,
  ImageBackground,
  type ImageSourcePropType,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '../../../context/ThemeContext';
import type { GamificationEvent } from '../../../types/auth';
import { GamificationShareButton } from '../../../components/gamification/GamificationShareButton';
import { uiFont } from '../../../styles/agapellaFonts';

import { accentTextOn } from '../../../styles/accentText';
/** CORE-703 — approved achievement_earned celebration catalogue. */
const ACHIEVEMENT_CELEBRATIONS: Record<string, { name: string; copy: string; icon: ImageSourcePropType }> = {
  first_spark: {
    name: 'Первый Огонёк', copy: '3 дня практики подряд',
    icon: require('../../../../assets/gamification/achievements/flame.png'),
  },
  week_of_light: {
    name: 'Неделя Света', copy: '7 дней практики подряд',
    icon: require('../../../../assets/gamification/achievements/soul-light.png'),
  },
  habit_sprout: {
    name: 'Росток Привычки', copy: '21 день практики подряд',
    icon: require('../../../../assets/gamification/achievements/sprout.png'),
  },
  forty_days: {
    name: 'Космический порядок', copy: '40 дней практики подряд',
    icon: require('../../../../assets/gamification/calendar-plus.png'),
  },
  sacred_mala: {
    name: 'Свет сознания', copy: '108 дней практики подряд',
    icon: require('../../../../assets/gamification/progress.png'),
  },
  wheel_of_year: {
    name: 'Световой год', copy: '365 дней практики подряд',
    icon: require('../../../../assets/gamification/achievements/shield-star.png'),
  },
  first_practice: {
    name: 'Первая практика', copy: 'Первая практика завершена',
    icon: require('../../../../assets/gamification/achievements/sprout.png'),
  },
  first_live: {
    name: 'Первый Live', copy: 'Первое участие в Live',
    icon: require('../../../../assets/gamification/achievements/live-discover.png'),
  },
};

const BACKGROUND = {
  light: require('../../../../assets/gamification/milestone-background-light.webp'),
  dark: require('../../../../assets/gamification/milestone-background-dark.webp'),
};

const BRAND_FONT = Platform.select({ web: 'var(--ag-font-brand)', ios: 'Georgia', default: 'serif' });

interface Props {
  visible: boolean;
  onClose: () => void;
  event: GamificationEvent | null;
}

export const AchievementUnlockedModal: React.FC<Props> = ({ visible, onClose, event }) => {
  const { isDark } = useTheme();
  const { width, height } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const closeRef = useRef<any>(null);

  useEffect(() => {
    if (Platform.OS !== 'web' || !visible || typeof document === 'undefined') return undefined;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const frame = requestAnimationFrame(() => closeRef.current?.focus?.());
    return () => {
      cancelAnimationFrame(frame);
      previouslyFocused?.focus?.();
    };
  }, [visible]);

  if (!event) return null;

  const mobile = width <= 520;
  const safeTop = Platform.OS === 'web' ? 0 : insets.top;
  const safeBottom = Platform.OS === 'web' ? 0 : insets.bottom;
  const gutter = mobile ? 8 : 22;
  const cardHeight = Math.max(260, Math.min(686, height - gutter * 2 - safeTop - safeBottom));
  const achievement = event.referenceKey ? ACHIEVEMENT_CELEBRATIONS[event.referenceKey] : undefined;
  const name = achievement?.name ?? event.title ?? 'Новое достижение';
  const copy = achievement?.copy ?? event.message ?? 'Достижение открыто';
  const icon = achievement?.icon ?? ACHIEVEMENT_CELEBRATIONS.first_spark.icon;
  const foreground = isDark ? '#F4EEE8' : '#2B2724';
  const muted = isDark ? '#B9AEA6' : '#746C66';
  const surface = isDark ? '#0D1218' : '#FFFCF7';
  const line = isDark ? 'rgba(235,137,59,0.42)' : 'rgba(225,128,49,0.34)';
  const rewardSurface = isDark ? 'rgba(22,28,35,0.82)' : 'rgba(255,255,255,0.72)';

  return (
    <Modal transparent visible={visible} animationType="fade" onRequestClose={onClose} statusBarTranslucent>
      <Pressable
        style={[
          styles.scrim,
          { paddingTop: gutter + safeTop, paddingBottom: gutter + safeBottom, paddingHorizontal: gutter },
        ]}
        onPress={onClose}
        accessible={false}
      >
        <Pressable
          onPress={(pressEvent) => pressEvent.stopPropagation()}
          accessible={false}
          accessibilityViewIsModal
          style={[styles.card, mobile && styles.cardMobile, { height: cardHeight, backgroundColor: surface, borderColor: line }]}
        >
          <ImageBackground
            source={isDark ? BACKGROUND.dark : BACKGROUND.light}
            resizeMode="cover"
            style={styles.background}
          >
            <LinearGradient
              pointerEvents="none"
              colors={isDark
                ? ['rgba(13,18,24,0.91)', 'rgba(13,18,24,0.72)', 'rgba(13,18,24,0.96)']
                : ['rgba(255,253,249,0.91)', 'rgba(255,251,245,0.76)', 'rgba(255,252,248,0.95)']}
              locations={[0, 0.46, 1]}
              style={StyleSheet.absoluteFillObject}
            />

            <TouchableOpacity
              ref={closeRef}
              onPress={onClose}
              activeOpacity={0.75}
              hitSlop={8}
              accessibilityRole="button"
              accessibilityLabel="Закрыть"
              style={[styles.close, mobile && styles.closeMobile, { borderColor: line, backgroundColor: surface }]}
            >
              <Ionicons name="close" size={22} color={accentTextOn('#EF791F')} />
            </TouchableOpacity>

            <ScrollView
              bounces={false}
              showsVerticalScrollIndicator={false}
              style={styles.scroll}
              contentContainerStyle={[styles.content, mobile && styles.contentMobile]}
            >
              <View style={styles.main}>
                <Text style={styles.eyebrow}>Новое достижение</Text>
                <Text accessibilityRole="header" style={[styles.title, mobile && styles.titleMobile, { color: foreground }]}>Поздравляем!</Text>
                <Text style={[styles.lead, { color: muted }]}>Открыто новое достижение</Text>

                <View style={[styles.hero, mobile && styles.heroMobile, { borderColor: line }]}>
                  <View style={[styles.heroInner, mobile && styles.heroInnerMobile, { borderColor: line }]}>
                    <Image source={icon} resizeMode="contain" style={[styles.heroIcon, mobile && styles.heroIconMobile]} accessibilityLabel={`Бейдж «${name}»`} />
                  </View>
                </View>

                <Text style={[styles.name, mobile && styles.nameMobile]}>{name}</Text>
                <Text style={[styles.copy, { color: muted }]}>{copy}</Text>

                <View style={styles.rewardBlock}>
                  <Text style={[styles.rewardLabel, { color: muted }]}>Награда</Text>
                  <View
                    accessible
                    accessibilityLabel={`Награда. Бейдж «${name}» добавлен в коллекцию достижений`}
                    style={[styles.reward, { borderColor: line, backgroundColor: rewardSurface }]}
                  >
                    <Image source={icon} resizeMode="contain" style={styles.rewardIcon} accessible={false} />
                    <View style={styles.rewardText}>
                      <Text style={[styles.rewardTitle, { color: foreground }]}>Бейдж «{name}»</Text>
                      <Text style={[styles.rewardCopy, { color: muted }]}>Добавлен в коллекцию достижений</Text>
                    </View>
                  </View>
                </View>
              </View>

              <View style={[styles.actions, mobile && styles.actionsMobile]}>
                <TouchableOpacity
                  style={[styles.secondary, mobile && styles.actionMobile, { borderColor: line, backgroundColor: surface }]}
                  onPress={onClose}
                  activeOpacity={0.85}
                  accessibilityRole="button"
                  accessibilityLabel="Продолжить путь"
                >
                  <Text style={[styles.secondaryText, { color: foreground }]}>Продолжить путь</Text>
                </TouchableOpacity>
                <LinearGradient colors={['#E96A16', '#F4932D']} style={[styles.primaryShell, mobile && styles.actionMobile]}>
                  <GamificationShareButton
                    type="achievement"
                    id={event.referenceKey ?? name}
                    filled
                    fallbackMessage={`В Agapella открыто новое достижение — «${name}»`}
                    style={styles.primary}
                    textStyle={styles.primaryText}
                  />
                </LinearGradient>
              </View>
            </ScrollView>
          </ImageBackground>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

const styles = StyleSheet.create({
  scrim: { flex: 1, backgroundColor: 'rgba(0,0,0,0.64)', justifyContent: 'center', alignItems: 'center' },
  card: { width: '100%', maxWidth: 620, borderRadius: 26, borderWidth: 1, overflow: 'hidden' },
  cardMobile: { borderRadius: 22 },
  background: { flex: 1 },
  close: { position: 'absolute', top: 22, right: 22, zIndex: 3, width: 36, height: 36, borderRadius: 18, borderWidth: 1, alignItems: 'center', justifyContent: 'center' },
  closeMobile: { top: 14, right: 14 },
  scroll: { flex: 1 },
  content: { flexGrow: 1, paddingHorizontal: 42, paddingTop: 34, paddingBottom: 34 },
  contentMobile: { paddingHorizontal: 22, paddingTop: 32, paddingBottom: 22 },
  main: { alignItems: 'center' },
  eyebrow: { color: accentTextOn('#E87422'), ...uiFont(500, 700), fontSize: 12, lineHeight: 17, letterSpacing: 1.14, textTransform: 'uppercase' },
  title: { marginTop: 5, fontFamily: BRAND_FONT, fontSize: 36, lineHeight: 46, ...uiFont(500), letterSpacing: -0.54, textAlign: 'center' },
  titleMobile: { ...uiFont(400), fontSize: 32, lineHeight: 41 },
  lead: { marginTop: 3, ...uiFont(400), fontSize: 14, lineHeight: 21 },
  hero: { width: 172, height: 172, borderRadius: 86, borderWidth: 1, marginTop: 22, alignItems: 'center', justifyContent: 'center', shadowColor: '#EB7C24', shadowOpacity: 0.22, shadowRadius: 21 },
  heroMobile: { width: 154, height: 154, borderRadius: 77, marginTop: 17 },
  heroInner: { width: 146, height: 146, borderRadius: 73, borderWidth: 1, alignItems: 'center', justifyContent: 'center' },
  heroInnerMobile: { width: 132, height: 132, borderRadius: 66 },
  heroIcon: { width: 158, height: 158 },
  heroIconMobile: { width: 142, height: 142 },
  name: { marginTop: 13, color: accentTextOn('#D96416'), fontFamily: BRAND_FONT, fontSize: 30, lineHeight: 39, ...uiFont(500), textAlign: 'center' },
  nameMobile: { ...uiFont(400), fontSize: 27, lineHeight: 35 },
  copy: { marginTop: 1, ...uiFont(400), fontSize: 14, lineHeight: 21, textAlign: 'center' },
  rewardBlock: { width: '100%', marginTop: 25 },
  rewardLabel: { ...uiFont(500, 700), fontSize: 11, lineHeight: 16, letterSpacing: 0.88, textTransform: 'uppercase' },
  reward: { width: '100%', marginTop: 7, paddingVertical: 13, paddingHorizontal: 16, flexDirection: 'row', gap: 13, alignItems: 'center', borderWidth: 1, borderRadius: 16 },
  rewardIcon: { width: 54, height: 54 },
  rewardText: { flex: 1 },
  rewardTitle: { ...uiFont(500, 700), fontSize: 14, lineHeight: 19 },
  rewardCopy: { marginTop: 2, ...uiFont(400), fontSize: 12, lineHeight: 17 },
  actions: { width: '100%', marginTop: 'auto', paddingTop: 25, flexDirection: 'row', gap: 12 },
  actionsMobile: { paddingTop: 20 },
  secondary: { flex: 1, minHeight: 48, borderWidth: 1, borderRadius: 13, alignItems: 'center', justifyContent: 'center' },
  primaryShell: { flex: 1.15, minHeight: 48, borderRadius: 13, overflow: 'hidden', shadowColor: '#DA5D07', shadowOpacity: 0.2, shadowRadius: 10 },
  primary: { flex: 1, minHeight: 48, borderRadius: 13, flexDirection: 'row', gap: 9, alignItems: 'center', justifyContent: 'center', backgroundColor: 'transparent' },
  secondaryText: { ...uiFont(500, 700), fontSize: 13, lineHeight: 18 },
  primaryText: { color: '#FFFFFF', ...uiFont(500, 700), fontSize: 13, lineHeight: 18 },
  actionMobile: { minHeight: 46 },
});
